import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  alarmCorrectionModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { DeviceApi } from '@/sdk/DeviceApi';
import ModalType1 from '@/modal/ModalType1';
import AddressModal from './AddressModal';
import {
  GetDeviceAlarmDtoRx,
  GetDeviceCalibrationDtoRx,
  UpdateDeviceAlarmAndCalibrationDtoTx,
} from '@/sdk/data-contracts';

const AlarmCorrectionModifyModal = () => {
  const { id } = useParams<{ id: string }>();
  const deviceApi = new DeviceApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setAlarmCorrectionModifyModalState = useSetRecoilState(
    alarmCorrectionModifyModalRecoil,
  );

  const [smsReceptionCycle, setSmsReceptionCycle] = useState<string>('');
  const [alarmStandards, setAlarmStandards] = useState<GetDeviceAlarmDtoRx[]>(
    [],
  );
  const [calibrations, setCalibrations] = useState<GetDeviceCalibrationDtoRx[]>(
    [],
  );

  const fetchAlarmCorrectionModify = async () => {
    try {
      const { data } = await deviceApi.deviceControllerGetDeviceSetting(
        Number(id),
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setSmsReceptionCycle(String(data.smsReceptionCycle));
      setAlarmStandards(data.alarmStandards);
      setCalibrations(data.calibrations);
    } catch (err: any) {}
  };

  const handleInputChange = (
    index: number,
    value: string,
    fieldName: string,
  ) => {
    if (fieldName === 'alarmTitle') {
      setAlarmStandards((prevState) =>
        prevState.map((item, i) =>
          i === index ? { ...item, name: value } : item,
        ),
      );
    }

    if (fieldName === 'alarmValue') {
      setAlarmStandards((prevState) =>
        prevState.map((item, i) =>
          i === index ? { ...item, value: value } : item,
        ),
      );
    }

    if (fieldName === 'calibrationsTitle') {
      setCalibrations((prevState) =>
        prevState.map((item, i) =>
          i === index ? { ...item, name: value } : item,
        ),
      );
    }

    if (fieldName === 'calibrationsValue') {
      setCalibrations((prevState) =>
        prevState.map((item, i) =>
          i === index ? { ...item, formula: value } : item,
        ),
      );
    }
  };

  const handleSubmit = async () => {
    const dto: UpdateDeviceAlarmAndCalibrationDtoTx = {
      smsReceptionCycle: Number(smsReceptionCycle),
      alarmStandards,
      calibrations,
    };
    try {
      await deviceApi.deviceControllerUpdateSetting(Number(id), dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    fetchAlarmCorrectionModify();
  }, []);

  return (
    <>
      <ModalType1 width={800}>
        <div className="title-box flex aic jcsb">
          <h1>알림 교정값 수정</h1>
          <button
            className="x-btn img-box"
            onClick={() => setAlarmCorrectionModifyModalState(false)}
          >
            <img src="/img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-area flex fdc">
          <div className="cnt-wrap flex fdc">
            <div className="cnt-box flex fdc">
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>SMS 수신주기</th>
                      <td>
                        <div className="td-box">
                          <IS.SelectType1 style={{ width: 115 }}>
                            <select
                              value={smsReceptionCycle}
                              onChange={(e) =>
                                setSmsReceptionCycle(e.target.value)
                              }
                              className="selected"
                            >
                              <option value="2">2시간</option>
                              <option value="4">4시간</option>
                              <option value="6">6시간</option>
                              <option value="8">8시간</option>
                              <option value="10">10시간</option>
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                          <span className="explanation-text">
                            초기값은 2시간 간격이며, SMS 수신자는 시설물의
                            책임관리자입니다.
                          </span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">알림 기준값</h2>
                </div>
              </div>
              <CS.TableType1>
                <table>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>데이터명</th>
                      <th>값</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alarmStandards.map((list, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 120 }}
                            type="text"
                            value={list.name}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                e.target.value,
                                'alarmTitle',
                              )
                            }
                          />
                        </td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 524 }}
                            type="text"
                            value={list.value}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                e.target.value,
                                'alarmValue',
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CS.TableType1>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">교정값 설정</h2>
                </div>
              </div>
              <CS.TableType1>
                <table>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>데이터명</th>
                      <th>값</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calibrations.map((list, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 120 }}
                            type="text"
                            value={list.name}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                e.target.value,
                                'calibrationsTitle',
                              )
                            }
                          />
                        </td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 524 }}
                            type="text"
                            value={list.formula}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                e.target.value,
                                'calibrationsValue',
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CS.TableType1>
            </div>
          </div>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setAlarmCorrectionModifyModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4 type="button" onClick={handleSubmit}>
              수정
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
    </>
  );
};

export default AlarmCorrectionModifyModal;
